import React, { useState } from "react";
import axios from "axios";

const ChangePassword = () => {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_new_password: "",
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /* ---------------- PASSWORD VALIDATION ---------------- */
  const passwordRules = {
    length: form.new_password.length >= 8,
    upper: /[A-Z]/.test(form.new_password),
    lower: /[a-z]/.test(form.new_password),
    number: /[0-9]/.test(form.new_password),
    special: /[!@#$%^&*]/.test(form.new_password),
    match:
      form.new_password &&
      form.confirm_new_password &&
      form.new_password === form.confirm_new_password,
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setMessage("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!isPasswordValid) {
      setError("Please meet all password requirements.");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      const res = await axios.post(
        "/api/users/change-password/",
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.success) {
        setMessage("Password changed successfully.");
        setForm({
          current_password: "",
          new_password: "",
          confirm_new_password: "",
        });
      }
    } catch (err) {
      const detail =
        err.response?.data?.error || "Something went wrong. Try again.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-page">
      <div className="cp-card">
        <h2 className="cp-title">Change Password</h2>

        {message && <p className="cp-success">{message}</p>}
        {error && <p className="cp-error">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="cp-group">
            <label>Current Password</label>
            <input
              type="password"
              name="current_password"
              className="cp-input"
              value={form.current_password}
              onChange={handleChange}
              required
            />
          </div>

          <div className="cp-group">
            <label>New Password</label>
            <input
              type="password"
              name="new_password"
              className="cp-input"
              value={form.new_password}
              onChange={handleChange}
              required
            />
          </div>

          <div className="cp-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              name="confirm_new_password"
              className="cp-input"
              value={form.confirm_new_password}
              onChange={handleChange}
              required
            />
          </div>

          {/* PASSWORD RULES */}
          <div className="cp-rules">
            <p>Password must contain:</p>

            <Rule valid={passwordRules.length} text="Minimum 8 characters" />
            <Rule valid={passwordRules.upper} text="At least 1 uppercase letter" />
            <Rule valid={passwordRules.lower} text="At least 1 lowercase letter" />
            <Rule valid={passwordRules.number} text="At least 1 number" />
            <Rule valid={passwordRules.special} text="At least 1 special character (!@#$%^&*)" />
            <Rule valid={passwordRules.match} text="Passwords must match" />
          </div>

          <button
            type="submit"
            className="cp-button"
            disabled={loading || !isPasswordValid}
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>

      {/* MODERNIZED CSS */}
      <style>{`
        .cp-page {
          padding: 40px;
          display: flex;
          justify-content: center;
          align-items: center;
          background: linear-gradient(135deg, #f6f8fb, #e8edf7);
          min-height: 100vh;
          font-family: "Segoe UI", Roboto, sans-serif;
        }

        .cp-card {
          background: #ffffff;
          padding: 30px 35px;
          width: 420px;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
          animation: fadeIn 0.4s ease;
        }

        .cp-title {
          margin-bottom: 20px;
          font-size: 24px;
          font-weight: 700;
          color: #0f1d60;
          text-align: center;
        }

        .cp-group {
          margin-bottom: 15px;
          display: flex;
          flex-direction: column;
        }

        label {
          font-weight: 600;
          margin-bottom: 6px;
          color: #0f1d60;
        }

        .cp-input {
          padding: 12px;
          border-radius: 10px;
          border: 1px solid #cbd5e1;
          background: #f8fafc;
          transition: 0.2s;
        }

        .cp-input:focus {
          border-color: #2563eb;
          background: #ffffff;
          box-shadow: 0 0 6px rgba(37, 99, 235, 0.25);
          outline: none;
        }

        .cp-button {
          width: 100%;
          padding: 14px;
          background: #0f1d60;
          color: #fff;
          border-radius: 10px;
          border: none;
          font-size: 16px;
          font-weight: 600;
          margin-top: 15px;
          cursor: pointer;
          transition: 0.3s ease;
        }

        .cp-button:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }

        .cp-button:hover:not(:disabled) {
          background: #1b2f8b;
          transform: scale(1.02);
        }

        .cp-success {
          color: #065f46;
          background: #d1fae5;
          padding: 10px;
          border-radius: 8px;
          margin-bottom: 10px;
        }

        .cp-error {
          color: #991b1b;
          background: #fee2e2;
          padding: 10px;
          border-radius: 8px;
          margin-bottom: 10px;
        }

        .cp-rules {
          margin-top: 10px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .cp-rule {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 5px 0;
        }

        .cp-rule.valid span {
          color: #15803d;
        }

        .cp-rule.invalid span {
          color: #b91c1c;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

/* Rule Component */
const Rule = ({ valid, text }) => {
  return (
    <div className={`cp-rule ${valid ? "valid" : "invalid"}`}>
      <span>{valid ? "✔" : "✖"}</span>
      <span>{text}</span>
    </div>
  );
};

export default ChangePassword;
